import React, { useEffect, useState } from 'react';
import { Header } from '@/components/layout/Header';
import { SecurityScoreGauge } from '@/components/dashboard/SecurityScoreGauge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { securityScoreAPI } from '@/lib/api';
import { cn, getScoreColor } from '@/lib/utils';
import { TrendingUp, Target, AlertTriangle, CheckCircle } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export function SecurityScorePage() {
  const [scoreData, setScoreData] = useState(null);
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [scoreRes, historyRes] = await Promise.all([
        securityScoreAPI.get(),
        securityScoreAPI.getHistory(30)
      ]);
      setScoreData(scoreRes.data);
      setHistory(historyRes.data.history.reverse());
    } catch (error) {
      console.error('Error loading security score:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const categories = scoreData?.categories ? Object.entries(scoreData.categories).map(([key, data]) => ({
    key,
    ...data
  })) : [];

  return (
    <div className="min-h-screen" data-testid="security-score-page">
      <Header title="Security Score" />
      
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Main Score */}
          <Card className="col-span-12 lg:col-span-4 bg-card/50 border-border">
            <CardContent className="pt-6">
              <SecurityScoreGauge 
                score={scoreData?.total_score || 0}
                trend={5}
              />
            </CardContent>
          </Card>

          {/* History Chart */}
          <Card className="col-span-12 lg:col-span-8 bg-card/50 border-border">
            <CardHeader>
              <CardTitle className="font-display text-sm uppercase tracking-wider text-muted-foreground">
                Histórico (30 dias)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={history}>
                    <defs>
                      <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis 
                      dataKey="calculated_at" 
                      tickFormatter={(v) => new Date(v).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                      stroke="#64748b"
                      fontSize={12}
                    />
                    <YAxis domain={[0, 100]} stroke="#64748b" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#0f172a', 
                        border: '1px solid #1e293b',
                        borderRadius: '4px'
                      }}
                      labelFormatter={(v) => new Date(v).toLocaleDateString('pt-BR')}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="total_score" 
                      stroke="#10b981" 
                      fillOpacity={1} 
                      fill="url(#scoreGradient)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Category Breakdown */}
          <Card className="col-span-12 bg-card/50 border-border">
            <CardHeader>
              <CardTitle className="font-display text-sm uppercase tracking-wider text-muted-foreground">
                Breakdown por Categoria
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                {categories.map((cat) => {
                  const percentage = (cat.score / cat.max_score) * 100;
                  return (
                    <div key={cat.key} className="bg-secondary/50 rounded-sm p-4 border border-border">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-display uppercase tracking-wider text-muted-foreground">
                          {cat.label}
                        </span>
                        <span className={cn("font-mono font-bold", getScoreColor(percentage))}>
                          {cat.score}/{cat.max_score}
                        </span>
                      </div>
                      <Progress value={percentage} className="h-2" />
                      <p className={cn("text-right text-sm font-mono mt-2", getScoreColor(percentage))}>
                        {Math.round(percentage)}%
                      </p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Recommendations */}
          <Card className="col-span-12 bg-card/50 border-border">
            <CardHeader>
              <CardTitle className="font-display text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                <Target className="h-4 w-4" />
                Recomendações para Melhorar
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {scoreData?.recommendations?.map((rec, i) => (
                  <div 
                    key={i} 
                    className="flex items-start gap-4 p-4 bg-secondary/50 rounded-sm border border-border hover:border-primary/50 transition-colors"
                  >
                    <div className={cn(
                      "p-2 rounded-sm",
                      rec.impact === 'high' ? 'bg-destructive/20' : 
                      rec.impact === 'medium' ? 'bg-warning/20' : 'bg-info/20'
                    )}>
                      {rec.impact === 'high' ? (
                        <AlertTriangle className="h-5 w-5 text-destructive" />
                      ) : (
                        <TrendingUp className="h-5 w-5 text-warning" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{rec.title}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-xs">
                          {rec.category}
                        </Badge>
                        <Badge className={cn(
                          "text-xs",
                          rec.impact === 'high' ? 'bg-destructive/20 text-destructive' :
                          rec.impact === 'medium' ? 'bg-warning/20 text-warning' : 'bg-info/20 text-info'
                        )}>
                          Impacto: {rec.impact}
                        </Badge>
                      </div>
                    </div>
                    <CheckCircle className="h-5 w-5 text-muted-foreground cursor-pointer hover:text-success transition-colors" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default SecurityScorePage;
